import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { Box, Typography } from '@mui/material';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useForm } from 'react-hook-form'
import { mergeSx } from 'merge-sx';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSaveOneRosterSettingsMutation } from '../../../../store/api/families.api';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { OneRosterSettingsDto } from '@families-link/shared';

export type OneRosterSettingsFamilyModalProps = CoreModalProps & {
	formTitle?: string;
	defaultValues?: any;
	formError?: any;
	submitButtonName?: string;
	closeModal(): void;
	districtId?: string | null;
	isFirstImport: boolean;
};

export const OneRosterSettingsFamilyModal: React.FC<OneRosterSettingsFamilyModalProps> = ({
	closeModal,
	districtId,
	isFirstImport, 
	...modalProps
}) => {
	const [t] = useTranslation();
	const [saveOneRosterSettings, { isLoading: loadingSubmit }] = useSaveOneRosterSettingsMutation();
	
	const {
		register,
		handleSubmit,
		formState: {errors, isSubmitting, isValid, isDirty, isSubmitted}
	} = useForm<OneRosterSettingsDto>({
		mode: "all"
	})
	
	const disableSubmit = !isValid && (isDirty || isSubmitted)

	const handleFormSubmit = async (values: OneRosterSettingsDto) => {
		try {
			const createOneRosterSettingsDto = {
				districtId: districtId ?? '',
				authUrl: values.authUrl,
				oneRosterUrl: values.oneRosterUrl,
				clientId: values.clientId,
				clientSecret: values.clientSecret
			}
			await saveOneRosterSettings(createOneRosterSettingsDto).unwrap()

			enqueueSnackbar('OneRoster settings have been saved', {
				variant: 'success',
				autoHideDuration: 3000
			})
			closeModal()
		} catch(requestError: any) {
			const serverError = requestError?.data?.message

			enqueueSnackbar(serverError, {
				variant: 'error',
				autoHideDuration: 4000
			})
			
		}
	}

	return (
		<CoreModal {...modalProps} modalWidth={710} bodySx={{ pt: 0, pb: 0 }} paperSx={{ width: '100%' }}>
			<Box sx={{ width: '100%', height: '100%' }}>
				<Typography variant="h5" sx={mergeSx(mainTitleModalWindowStyles, {
					position: 'sticky',
					top: 0,
					zIndex: 1,
					backgroundColor: (theme) => theme.colors.white,
				})}>
					{'Import source settings'}
				</Typography>

				<Box component="form" autoComplete='off' onSubmit={handleSubmit(handleFormSubmit)}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						{isFirstImport &&
							<Typography variant="p1">
								{'To import families from Student Information System, firstly, you have to link one by filling each field below:'}
							</Typography>
						}
						<CoreTextField
							placeholder={t('families.forms.placeholders.auth-url')}
							fullWidth={true}
							label={t('families.forms.labels.auth-url')}
							{...register('authUrl', { required: `${t('families.forms.labels.auth-url')} is required` })}
							error={!!errors.authUrl}
							helperText={errors.authUrl?.message}
						/>
						<CoreTextField
							placeholder={t('families.forms.placeholders.oneroster-url')}
							fullWidth={true}
							label={t('families.forms.labels.oneroster-url')}
							{...register('oneRosterUrl', { required: `${t('families.forms.labels.oneroster-url')} is required` })}
							error={!!errors.oneRosterUrl}
							helperText={errors.oneRosterUrl?.message}
						/>
						<CoreTextField
							placeholder={t('families.forms.placeholders.client-id')}
							fullWidth={true}
							label={t('families.forms.labels.client-id')}
							{...register('clientId', { required: `${t('families.forms.labels.client-id')} is required` })}
							error={!!errors.clientId}
							helperText={errors.clientId?.message}
						/>
						<CoreTextField
							placeholder={t('families.forms.placeholders.client-secret')}
							fullWidth={true}
							label={t('families.forms.labels.client-secret')}
							{...register('clientSecret', { required: `${t('families.forms.labels.client-secret')} is required` })}
							error={!!errors.clientSecret}
							helperText={errors.clientSecret?.message}
						/>
					</Box>
					<Box
						sx={{
							position: 'sticky',
							bottom: 0,
							zIndex: 1,
							backgroundColor: '#FFFFFF',
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							mt: 5,
							gap: 3,
							pb: 5
						}}
					>
						<CoreButton onClick={closeModal} variant="secondary" sx={mergeSx(buttonModalWindowStyles)}>
							{t('families.forms.buttons.cancel')}
						</CoreButton>
						<CoreButton
							contraModeStyleButton
							loading={loadingSubmit || isSubmitting}
							disabled={disableSubmit}
							type="submit"
							sx={buttonModalWindowStyles}
						>
							{t('families.forms.buttons.save')}
						</CoreButton>
					</Box>
				</Box>
			</Box>
		</CoreModal>
	);
};
